<template>
  <section class="py-16 md:py-24" id="how-it-works">
    <div class="w-[90%] mx-auto">
      <!-- Section Header -->
      <div class="text-center space-y-4 mb-12">
        <h2 class="text-2xl md:text-4xl font-bold text-gray-900">
          {{ $t('howItWorksClient.title') }}
        </h2>
        <div class="flex justify-center">
          <div class="h-1 w-16 bg-[#009B9D] rounded-full"></div>
        </div>
        <p class="text-gray-600 max-w-2xl mx-auto text-sm md:text-base">
          {{ $t('howItWorksClient.subtitle') }}
        </p>
      </div>

      <!-- Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <!-- Steps List -->
        <div class="space-y-4">
          <div 
            v-for="(step, index) in steps" 
            :key="index"
            @click="activeStep = index"
            class="group cursor-pointer"
          >
            <!-- Step Card -->
            <div 
              class="relative p-5 md:p-6 rounded-2xl transition-all duration-500 ease-out"
              :class="[
                activeStep === index 
                  ? 'bg-[#F8F5F2] shadow-lg shadow-[#009B9D]/10 scale-[1.02]' 
                  : 'bg-gray-50/50 hover:bg-gray-50'
              ]"
            >
              <!-- Active Indicator Bar -->
              <div 
                class="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-r-full bg-[#009B9D] transition-all duration-500"
                :class="activeStep === index ? 'h-3/4 opacity-100' : 'h-0 opacity-0'"
              ></div>

              <div class="flex items-start space-x-4">
                <!-- Step Number -->
                <div 
                  class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300"
                  :class="[
                    activeStep === index 
                      ? 'bg-[#009B9D] text-white shadow-lg shadow-[#009B9D]/30' 
                      : 'bg-[#009B9D]/10 text-[#009B9D] group-hover:bg-[#009B9D]/20'
                  ]"
                >
                  {{ index + 1 }}
                </div>

                <!-- Step Content -->
                <div class="flex-1">
                  <h3 
                    class="font-semibold text-lg md:text-xl transition-colors duration-300"
                    :class="activeStep === index ? 'text-[#009B9D]' : 'text-gray-900 group-hover:text-[#009B9D]'"
                  >
                    {{ $t(step.titleKey) }}
                  </h3>
                  <p 
                    class="mt-2 text-sm md:text-base transition-all duration-300"
                    :class="activeStep === index ? 'text-gray-700' : 'text-gray-500'"
                  >
                    {{ $t(step.descKey) }}
                  </p>
                </div>

                <!-- Arrow Icon -->
                <div 
                  class="flex-shrink-0 transition-all duration-300"
                  :class="activeStep === index ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-2'"
                >
                  <svg class="w-5 h-5 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </div>
              </div>
            </div>

            <!-- Connector Line -->
            <div 
              v-if="index < steps.length - 1" 
              class="ml-9 h-4 border-l-2 transition-colors duration-300"
              :class="activeStep > index ? 'border-[#009B9D]' : 'border-gray-200'"
            ></div>
          </div>
        </div>

        <!-- Phone Mockup -->
        <div class="hidden lg:flex justify-center items-center">
          <div class="relative">
            <!-- Decorative Elements -->
            <div class="absolute -top-8 -right-8 w-32 h-32 bg-[#009B9D]/10 rounded-full blur-3xl"></div>
            <div class="absolute -bottom-8 -left-8 w-40 h-40 bg-[#009B9D]/5 rounded-full blur-3xl"></div>
            
            <!-- Phone Frame - Style maquette -->
            <div class="relative z-10 w-[280px] h-[560px] bg-[#1a3a3b] rounded-[3rem] p-3 shadow-2xl">
              <!-- Screen -->
              <div class="w-full h-full bg-white rounded-[2.2rem] overflow-hidden">
                <transition
                  mode="out-in"
                  enter-active-class="transition-all duration-500 ease-out"
                  enter-from-class="opacity-0 scale-105"
                  enter-to-class="opacity-100 scale-100"
                  leave-active-class="transition-all duration-300 ease-in"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <img 
                    :key="activeStep"
                    :src="`/images/${steps[activeStep].image}`" 
                    :alt="$t(steps[activeStep].titleKey)"
                    class="w-full h-full object-cover object-top"
                  />
                </transition>
              </div>
            </div>

            <!-- Floating Badges -->
            <div 
              class="absolute -left-4 top-20 bg-white rounded-xl shadow-xl p-3 flex items-center space-x-2 animate-float"
              style="animation-delay: 0s;"
            >
              <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
                </svg>
              </div>
              <span class="text-xs font-medium text-gray-700">Commande confirmée</span>
            </div>

            <div 
              class="absolute -right-4 bottom-32 bg-white rounded-xl shadow-xl p-3 flex items-center space-x-2 animate-float"
              style="animation-delay: 0.5s;"
            >
              <div class="w-8 h-8 bg-[#009B9D]/20 rounded-full flex items-center justify-center">
                <svg class="w-4 h-4 text-[#009B9D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
                </svg>
              </div>
              <span class="text-xs font-medium text-gray-700">Ramassage aujourd'hui</span>
            </div>
          </div>
        </div>

        <!-- Mobile Image -->
        <div class="lg:hidden flex justify-center mt-8">
          <div class="w-48 h-auto rounded-3xl overflow-hidden shadow-xl">
            <transition
              mode="out-in"
              enter-active-class="transition-opacity duration-300"
              enter-from-class="opacity-0"
              enter-to-class="opacity-100"
              leave-active-class="transition-opacity duration-200"
              leave-from-class="opacity-100"
              leave-to-class="opacity-0"
            >
              <img 
                :key="activeStep"
                :src="`/images/${steps[activeStep].image}`" 
                :alt="$t(steps[activeStep].titleKey)"
                class="w-full h-full object-cover"
              />
            </transition>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
  name: 'HowItWorkClient',
  setup() {
    const activeStep = ref(0)
    let interval = null

    const steps = [
      {
        titleKey: 'howItWorksClient.steps.choose.title',
        descKey: 'howItWorksClient.steps.choose.description',
        image: 'place_order.png'
      },
      {
        titleKey: 'howItWorksClient.steps.prepare.title',
        descKey: 'howItWorksClient.steps.prepare.description',
        image: 'pickup.png'
      },
      {
        titleKey: 'howItWorksClient.steps.pay.title',
        descKey: 'howItWorksClient.steps.pay.description',
        image: 'cleaning.png'
      },
      {
        titleKey: 'howItWorksClient.steps.receive.title',
        descKey: 'howItWorksClient.steps.receive.description',
        image: 'delivery.png'
      }
    ]

    const startAutoCycle = () => {
      interval = setInterval(() => {
        activeStep.value = (activeStep.value + 1) % steps.length
      }, 4000)
    }

    const stopAutoCycle = () => {
      if (interval) {
        clearInterval(interval)
        interval = null
      }
    }

    onMounted(() => {
      startAutoCycle()
    })

    onUnmounted(() => {
      stopAutoCycle()
    })

    return {
      activeStep,
      steps
    }
  }
}
</script>

<style scoped>
@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}
</style>